// @ts-nocheck
/**
 * Radio Category Manager
 * Carrega categorias (landing) e renderiza seções com rádios.
 * Busca client-side sobre as rádios carregadas.
 */

function appradioPublicBasePath() {
    if (typeof window !== 'undefined' && window.APPRADIO_PUBLIC_BASE != null && window.APPRADIO_PUBLIC_BASE !== '') {
        return String(window.APPRADIO_PUBLIC_BASE).replace(/\/$/, '');
    }
    return '';
}

/** Path absoluto no site (respeita subpasta definida em index: APPRADIO_PUBLIC_BASE). */
function appradioPublicApiUrl(pathWithLeadingSlash) {
    const base = appradioPublicBasePath();
    const p = pathWithLeadingSlash.startsWith('/') ? pathWithLeadingSlash : `/${pathWithLeadingSlash}`;
    return `${base}${p}`;
}

function appradioRadioDetailPath(id, displayName) {
    const base = appradioPublicBasePath();
    const sid = String(Number(id) || 0);
    if (!sid || sid === '0') {
        return (base ? base : '') + '/';
    }
    let slug = '';
    if (displayName && typeof displayName === 'string') {
        let s = displayName;
        if (typeof s.normalize === 'function') {
            s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        }
        s = s.replace(/\./g, '');
        slug = s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80).replace(/-+$/g, '');
    }
    const path = slug ? `/radio/${sid}-${slug}` : `/radio/${sid}`;
    return base + path;
}

class RadioManager {
    constructor() {
        this.allCategories = [];
        this.displayedCategories = [];
        this.filteredCategories = [];
        this.searchQuery = '';
        this.searchDebounceTimer = null;
        this.activeSearchController = null;
        this.selectedSegment = '';
        this.selectedLocation = '';
        this.isLoading = false;
        // Limite inicial por categoria — proporcional à grade (4/3/2/1 colunas) × 3 linhas.
        // Calculado dinamicamente: ver computeLimitPerCategory().
        this.limitPerCategory = this.computeLimitPerCategory();
        // Estado de "load more" por categoria: { [key]: { loading: bool, exhausted: bool } }
        this.categoryLoadMoreState = {};
        // Render todas as categorias de uma vez (sem limitador / botão "Carregar mais").
        this.categoriesPerPage = Infinity;
        this.currentCategoryPage = 0;
        this.currentPlayingId = null;
        this.searchMode = 'categories'; // 'categories' ou 'server'
        this.availableSegments = [];
        this.searchOffset = 0;
        this.searchLimit = 10;
        this.searchTotal = 0;
        this.searchRadios = [];
        this.nearbyStations = null;
        this.favoritesCategory = null;
        this.favoritesCategoryKey = 'user-favorites';
        // Rate limiting para proteger servidor Shazam
        this.lastRadioChangeTime = 0;
        this.radioChangeCooldown = 5000; // 5 segundos entre trocas
        this.rateLimitToast = null;
        // Card atualmente em navegação para /radio/{id}
        this.currentNavigatingCard = null;
        // Cache LRU em memoria para queries repetidas (radios.pro)
        this.searchCache = new Map();
        this.searchCacheLimit = 30;
        this.loadErrorActive = false;
        this.loadErrorContext = null;
        this.init();
    }

    init() {
        this.clearNavigatingState();
        this.setupEventListeners();
        this.setupPageLifecycleHandlers();
        this.loadNearbyStations();
        this.loadCategories();
        this.loadSegments();
    }

    /** Remove overlay de navegação (ex.: voltar pelo histórico / bfcache). */
    clearNavigatingState() {
        document.querySelectorAll('.radio-card.is-navigating').forEach((card) => {
            card.classList.remove('is-navigating');
        });
        this.currentNavigatingCard = null;
    }

    /**
     * Esconde o fallback SSR (#rp-seo-radios) quando o grid dinâmico já
     * trouxe rádios do servidor. Em erro de carga, reexibe para o usuário
     * ainda ter links /radio/ sem JS.
     */
    setSeoRadiosFallbackVisible(visible) {
        const el = document.getElementById('rp-seo-radios');
        if (!el) return;
        el.classList.toggle('is-js-replaced', !visible);
        el.hidden = !visible;
    }

    setupPageLifecycleHandlers() {
        if (this._pageLifecycleBound || typeof window === 'undefined') return;
        this._pageLifecycleBound = true;

        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                this.clearNavigatingState();
                if (this.loadErrorActive) {
                    this.retryAfterLoadError();
                }
            }
        });

        window.addEventListener('online', () => {
            if (this.loadErrorActive) {
                this.retryAfterLoadError();
            }
        });
    }

    clearLoadError() {
        this.loadErrorActive = false;
        this.loadErrorContext = null;
    }

    retryAfterLoadError() {
        if (!this.loadErrorActive || this.isLoading) return;
        if (this.loadErrorContext === 'search') {
            this.searchServer();
            return;
        }
        this.loadCategories();
    }

    /**
     * Insere "Rádios Próximas" no topo apenas uma vez (ordem de chegada das respostas AJAX é indefinida).
     */
    prependNearbyCategoryIfReady() {
        if (!this.nearbyStations || !Array.isArray(this.allCategories)) return;
        const t = this.nearbyStations.title || '';
        const dup = this.allCategories.some((c) => c && c.title === t);
        if (dup) return;
        this.allCategories.unshift(this.nearbyStations);
    }

    getI18n(key, fallback) {
        const bag = typeof window !== 'undefined' ? window.RADIO_MANAGER_I18N : null;
        if (bag && bag[key]) return String(bag[key]);
        return fallback || '';
    }

    /** Normaliza favorito (Firestore novo/legado ou localStorage) para cards do site. */
    isFavoriteStreamLabel(text) {
        const s = String(text || '').trim();
        if (!s) return false;
        return /^stream\s*#/i.test(s) || /\(.*\d+\s*k\)/i.test(s);
    }

    normalizeFavoriteToRadio(favorite) {
        if (!favorite || typeof favorite !== 'object') return null;
        const id = favorite.id || favorite.radioId;
        if (!id) return null;

        const title =
            favorite.radio_name ||
            favorite.title ||
            favorite.nome ||
            favorite.name ||
            '';

        const logoRaw =
            favorite.logo_url ||
            favorite.url_logo ||
            favorite.logo ||
            favorite.urlLogo ||
            '';
        const logo = (typeof window !== 'undefined' && window.RadiosProFavorites)
            ? window.RadiosProFavorites.logoToUrl(logoRaw)
            : logoRaw;

        let localizacao = favorite.localizacao || favorite.location || '';
        if (this.isFavoriteStreamLabel(localizacao)) {
            localizacao = '';
        }

        let segmento = favorite.segmento || favorite.segment || favorite.category || '';
        if (this.isFavoriteStreamLabel(segmento)) {
            segmento = '';
        }

        const streams = Array.isArray(favorite.streams) ? favorite.streams.slice() : [];
        const streamUrl =
            favorite.url_stream_audio ||
            favorite.url_stream_audio_secondary ||
            favorite.streaming_url ||
            favorite.streamUrl ||
            favorite.stream_url ||
            '';
        if (!streams.length && streamUrl) {
            streams.push({ url: streamUrl });
        }

        return {
            id: String(id),
            title: title || 'Rádio',
            url_logo: logo,
            localizacao,
            segmento,
            color: favorite.color || '#f0f0f0',
            streams,
        };
    }

    fetchFavoritesRadios() {
        const hasServerSession = !!this.getCookie('RPRO_USER_UID');
        const mapLocal = () => {
            const seen = new Set();
            const out = [];
            this.getLocalFavorites().forEach((fav) => {
                const radio = this.normalizeFavoriteToRadio(fav);
                if (!radio || seen.has(radio.id)) return;
                seen.add(radio.id);
                out.push(radio);
            });
            return out;
        };

        if (!hasServerSession) {
            return Promise.resolve(mapLocal());
        }

        return fetch('/ajax/ajax-favorites.php?action=list', { credentials: 'same-origin' })
            .then((response) => response.json())
            .then((data) => {
                if (!data || !data.success || !Array.isArray(data.favorites)) {
                    return mapLocal();
                }
                const seen = new Set();
                const out = [];
                data.favorites.forEach((fav) => {
                    const radio = this.normalizeFavoriteToRadio(fav);
                    if (!radio || seen.has(radio.id)) return;
                    seen.add(radio.id);
                    out.push(radio);
                });
                if (out.length > 0) return out;
                return mapLocal();
            })
            .catch((err) => {
                return mapLocal();
            });
    }

    buildFavoritesCategory(radios) {
        const count = radios.length;
        const detailTpl = this.getI18n('favoritesDetailMany', '%d rádios salvas por você');
        const detail = count === 1
            ? this.getI18n('favoritesDetailOne', '1 rádio salva por você')
            : detailTpl.replace('%d', String(count));
        return {
            key: this.favoritesCategoryKey,
            title: this.getI18n('favoritesTitle', 'Minhas Rádios Favoritas'),
            detail,
            radios,
            total_items: count,
        };
    }

    async refreshFavoritesCategory() {
        const radios = await this.fetchFavoritesRadios();
        this.favoritesCategory = radios.length > 0 ? this.buildFavoritesCategory(radios) : null;
    }

    prependFavoritesCategoryIfReady() {
        if (!Array.isArray(this.allCategories)) return;
        this.allCategories = this.allCategories.filter(
            (c) => c && c.key !== this.favoritesCategoryKey
        );
        if (!this.favoritesCategory || !Array.isArray(this.favoritesCategory.radios)) return;
        if (this.favoritesCategory.radios.length === 0) return;
        this.allCategories.unshift(this.favoritesCategory);
    }

    async applyCategoriesWithFavorites() {
        await this.refreshFavoritesCategory();
        this.prependNearbyCategoryIfReady();
        this.prependFavoritesCategoryIfReady();
        this.filteredCategories = this.allCategories;
        this.currentCategoryPage = 0;
        this.renderCategories();
    }

    setupEventListeners() {
        const searchInput = document.querySelector('.figma-search-controls input[type="search"]');
        if (searchInput) {
            // Busca com debounce ao digitar
            searchInput.addEventListener('input', (e) => {
                this.searchQuery = e.target.value;
                this.currentCategoryPage = 0;

                // Debounce 500ms para não disparar busca antes de terminar de digitar
                if (this.searchDebounceTimer) {
                    clearTimeout(this.searchDebounceTimer);
                }

                this.searchDebounceTimer = setTimeout(() => {
                    this.searchDebounceTimer = null;
                    this.performSearch();
                }, 300);
            });

            // Busca imediata ao pressionar Enter
            searchInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    this.searchQuery = e.target.value;
                    this.currentCategoryPage = 0;

                    if (this.searchDebounceTimer) {
                        clearTimeout(this.searchDebounceTimer);
                        this.searchDebounceTimer = null;
                    }

                    this.performSearch();
                }
            });
        }

        const searchBtn = document.querySelector('.search-btn');
        if (searchBtn) {
            searchBtn.addEventListener('click', () => {
                this.currentCategoryPage = 0;
                this.performSearch();
            });
        }

        // Listeners para filtros
        const segmentSelect = document.querySelector('#filter-segment');
        if (segmentSelect) {
            segmentSelect.addEventListener('change', (e) => {
                this.selectedSegment = e.target.value;
                this.currentCategoryPage = 0;
                this.performSearch();
            });
        }

        const locationInput = document.querySelector('#filter-location');
        if (locationInput) {
            locationInput.addEventListener('input', (e) => {
                this.selectedLocation = e.target.value;
                this.currentCategoryPage = 0;

                // Mesmo debounce 500ms do campo de busca
                if (this.searchDebounceTimer) {
                    clearTimeout(this.searchDebounceTimer);
                }
                this.searchDebounceTimer = setTimeout(() => {
                    this.searchDebounceTimer = null;
                    this.performSearch();
                }, 300);
            });
        }

        document.addEventListener('radiospro:favorite-changed', () => {
            if (this.searchMode !== 'categories') return;
            this.applyCategoriesWithFavorites();
        });

        const loadMoreBtn = document.getElementById('load-more-btn');
        if (loadMoreBtn) {
            loadMoreBtn.addEventListener('click', () => {
                if (this.searchMode === 'categories') {
                    this.loadMoreCategories();
                } else if (this.searchMode === 'server') {
                    this.loadMoreSearchResults();
                }
            });
            loadMoreBtn.style.display = 'none';
        }
    }

    loadMoreCategories() {
        this.currentCategoryPage++;
        this.renderCategories();
    }

    /**
     * Calcula o limite por categoria proporcional à largura disponível:
     *  > 1260px → 4 colunas → 12 rádios (3 linhas)
     *  > 880px  → 3 colunas →  9 rádios
     *  > 520px  → 2 colunas →  6 rádios
     *  ≤ 520px  → 1 coluna  →  4 rádios (mínimo razoável p/ mobile)
     * Mantém-se em sincronia com os breakpoints do CSS (.radio-category-grid).
     */
    computeLimitPerCategory() {
        const w = (typeof window !== 'undefined' && window.innerWidth) || 1280;
        let cols = 1;
        if (w > 1260) cols = 4;
        else if (w > 880) cols = 3;
        else if (w > 520) cols = 2;
        return cols === 1 ? 4 : cols * 3;
    }

    /**
     * Carrega o próximo "bloco" de rádios de uma categoria do landing.
     * @param {string} key Chave da categoria (vinda do backend).
     * @param {HTMLElement} btn Botão clicado (para feedback visual).
     */
    loadMoreCategoryItems(key, btn) {
        if (!key) return;

        const category = (this.allCategories || []).find((c) => c && c.key === key);
        if (!category || !Array.isArray(category.radios)) return;

        const state = (this.categoryLoadMoreState[key] = this.categoryLoadMoreState[key] || {
            loading: false,
            exhausted: false,
        });
        if (state.loading || state.exhausted) return;

        const offset = category.radios.length;
        const total = Number(category.total_items || 0);
        if (total > 0 && offset >= total) {
            state.exhausted = true;
            this.removeCategoryLoadMoreButton(btn);
            return;
        }

        const limit = this.computeLimitPerCategory();

        state.loading = true;
        if (btn) {
            btn.classList.add('is-loading');
            btn.disabled = true;
        }

        const params = new URLSearchParams({
            action: 'get-landing-category-items',
            key,
            offset,
            limit,
        });

        fetch(appradioPublicApiUrl(`/ajax/radio-search.php?${params}`))
            .then((r) => r.json())
            .then((data) => {
                if (!data || !data.success || !Array.isArray(data.radios)) {
                    throw new Error('invalid_payload');
                }
                this.appendRadiosToCategory(key, data.radios, btn);
                if (typeof data.total_items === 'number') {
                    category.total_items = data.total_items;
                }
                const newTotalLoaded = category.radios.length;
                const hasMore =
                    typeof data.has_more === 'boolean'
                        ? data.has_more
                        : newTotalLoaded < Number(category.total_items || 0);
                if (!hasMore) {
                    state.exhausted = true;
                    this.removeCategoryLoadMoreButton(btn);
                }
            })
            .catch((err) => {
                console.error('[RadioManager] Falha ao carregar mais da categoria:', err);
            })
            .finally(() => {
                state.loading = false;
                if (btn && !state.exhausted) {
                    btn.classList.remove('is-loading');
                    btn.disabled = false;
                }
            });
    }

    /**
     * Anexa novos cards de rádio ao grid existente da categoria (sem re-renderizar tudo)
     * e atualiza category.radios em this.allCategories.
     */
    appendRadiosToCategory(key, newRadios, btn) {
        const category = (this.allCategories || []).find((c) => c && c.key === key);
        if (!category) return;

        // Deduplicar por id (defensivo contra cliques duplos)
        const existingIds = new Set((category.radios || []).map((r) => String(r.id)));
        const toAdd = newRadios.filter((r) => r && r.id != null && !existingIds.has(String(r.id)));
        if (toAdd.length === 0) return;

        category.radios = [...(category.radios || []), ...toAdd];

        // Localizar o grid correspondente (o botão está na mesma section)
        const section = btn ? btn.closest('.radio-category-section') : null;
        const grid = section ? section.querySelector('.radio-category-grid') : null;
        if (!grid) {
            // Fallback: re-render completo (raro)
            this.renderCategories();
            return;
        }

        const baseIndex = grid.querySelectorAll('.radio-card.is-visible').length;
        toAdd.forEach((radio) => {
            const card = this.createRadioCard(radio);
            grid.appendChild(card);
        });

        this.setupRadioCardListeners();
        this.loadFavoriteStates();
        this.staggerRevealCards(grid, { baseIndex, sections: false });
    }

    removeCategoryLoadMoreButton(btn) {
        if (!btn) return;
        const wrapper = btn.closest('.radio-category-load-more');
        if (wrapper && wrapper.parentNode) {
            wrapper.parentNode.removeChild(wrapper);
        }
    }

    loadMoreSearchResults() {
        this.searchOffset += this.searchLimit;
        this.searchServer();
    }

    updateLoadMoreButton() {
        const loadMoreBtn = document.getElementById('load-more-btn');
        if (!loadMoreBtn) return;

        const totalDisplayed = (this.currentCategoryPage + 1) * this.categoriesPerPage;
        const totalCategories = this.filteredCategories.length;

        if (totalDisplayed < totalCategories) {
            loadMoreBtn.style.display = 'block';
            loadMoreBtn.disabled = false;
            loadMoreBtn.textContent = 'Carregar mais categorias';
        } else {
            loadMoreBtn.style.display = 'none';
        }
    }

    loadSegments() {
        const params = new URLSearchParams({
            action: 'get-segments'
        });

        fetch(appradioPublicApiUrl(`/ajax/radio-search.php?${params}`))
            .then((response) => response.json())
            .then((data) => {
                if (data.success && Array.isArray(data.segments)) {
                    this.availableSegments = data.segments;
                    this.populateSegmentFilter();
                }
            })
            .catch((err) => console.error('Erro ao carregar segmentos', err));
    }

    loadNearbyStations() {
        const params = new URLSearchParams({
            action: 'get-nearby-stations',
            limit: 15
        });

        fetch(appradioPublicApiUrl(`/ajax/radio-search.php?${params}`))
            .then((response) => response.json())
            .then((data) => {
                if (data.success && Array.isArray(data.radios) && data.radios.length > 0) {
                    this.nearbyStations = {
                        title: 'Rádios Próximas',
                        detail: `${data.radios.length} estação(ões) disponível(is)`,
                        radios: data.radios
                    };
                    if (this.allCategories.length > 0 && this.searchMode === 'categories') {
                        this.applyCategoriesWithFavorites();
                    }
                }
            })
            .catch((err) => console.error('Erro ao carregar rádios próximas', err));
    }

    loadCategories() {
        if (this.isLoading) return;
        this.isLoading = true;
        this.showLoading(true);

        const params = new URLSearchParams({
            action: 'get-landing-categories',
            limit: this.limitPerCategory
        });

        fetch(appradioPublicApiUrl(`/ajax/radio-search.php?${params}`))
            .then((response) => {
                if (!response.ok) throw new Error('http_' + response.status);
                return response.json();
            })
            .then((data) => {
                if (!data.success) throw new Error('Falha ao carregar categorias');
                this.clearLoadError();
                this.allCategories = data.categories || [];
                return this.applyCategoriesWithFavorites();
            })
            .catch((err) => {
                console.error('Erro ao carregar categorias', err);
                this.showLoadError('categories');
            })
            .finally(() => {
                this.isLoading = false;
                this.showLoading(false);
            });
    }

    populateSegmentFilter() {
        const segmentSelect = document.querySelector('#filter-segment');
        if (!segmentSelect || this.availableSegments.length === 0) return;

        const currentValue = segmentSelect.value;
        segmentSelect.innerHTML = '<option value="">Todos os segmentos</option>';
        
        this.availableSegments.forEach((seg) => {
            const option = document.createElement('option');
            option.value = seg;
            option.textContent = seg;
            segmentSelect.appendChild(option);
        });

        if (currentValue) {
            segmentSelect.value = currentValue;
        }
    }

    performSearch() {
        const query = this.searchQuery.trim();
        const hasFilters = this.selectedSegment || this.selectedLocation;

        // Evita chamadas com 0 ou 1 caractere quando não há filtros
        if (!hasFilters && query.length < 2) {
            // Cancela requisição em andamento ao voltar ao modo categorias
            this.abortActiveSearch();
            this.searchMode = 'categories';
            this.loadCategories();
            return;
        }

        // Se tem filtros ou busca com 2+ caracteres, usar busca via servidor
        this.searchMode = 'server';
        this.searchOffset = 0;
        this.searchRadios = [];
        this.searchServer();
    }

    abortActiveSearch() {
        if (this.activeSearchController) {
            try {
                this.activeSearchController.abort();
            } catch (err) {
                console.warn('[RadioManager] Abort falhou:', err);
            }
            this.activeSearchController = null;
        }
    }

    setSearchInputLoading(isLoading) {
        const pill = document.querySelector('.search-pill');
        if (!pill) return;
        pill.classList.toggle('is-loading', !!isLoading);
    }

    cacheKeyForSearch(query) {
        return `${query}||${this.selectedSegment || ''}||${this.selectedLocation || ''}||${this.searchOffset}|${this.searchLimit}`;
    }

    getCachedSearch(key) {
        if (!this.searchCache.has(key)) return null;
        const value = this.searchCache.get(key);
        this.searchCache.delete(key);
        this.searchCache.set(key, value);
        return value;
    }

    setCachedSearch(key, value) {
        if (this.searchCache.has(key)) this.searchCache.delete(key);
        this.searchCache.set(key, value);
        while (this.searchCache.size > this.searchCacheLimit) {
            const oldestKey = this.searchCache.keys().next().value;
            this.searchCache.delete(oldestKey);
        }
    }

    async searchServer() {
        // Se já existe uma busca em andamento, aborta antes de iniciar outra
        if (this.isLoading) {
            this.abortActiveSearch();
        }

        const query = this.searchQuery.trim();
        const hasFilters = this.selectedSegment || this.selectedLocation;
        // Proteção extra: não busca se não houver filtros e query < 2
        if (!hasFilters && query.length < 2) {
            return;
        }

        // Cache hit: responde instantaneo sem rede
        const cacheKey = this.cacheKeyForSearch(query);
        const cached = this.getCachedSearch(cacheKey);
        if (cached) {
            this.searchTotal = cached.searchTotal;
            this.searchRadios = this.searchOffset === 0 ? cached.radios : [...this.searchRadios, ...cached.radios];
            this.filteredCategories = this.convertRadiosToCategories(this.searchRadios);
            this.currentCategoryPage = 0;
            this.renderSearchResults();
            return;
        }

        this.isLoading = true;
        this.showLoading(true);
        this.setSearchInputLoading(true);

        const params = new URLSearchParams({
            action: 'search-server',
            title: query,
            segmento: this.selectedSegment,
            localizacao: this.selectedLocation,
            limit: this.searchLimit,
            offset: this.searchOffset
        });

        const url = appradioPublicApiUrl(`/ajax/radio-search.php?${params}`);

        // Cancela requisição anterior antes de iniciar uma nova
        this.abortActiveSearch();

        const myController = new AbortController();
        this.activeSearchController = myController;
        const { signal } = myController;
        const queryAtRequest = query; // para ignorar resposta obsoleta
        const cacheKeyAtRequest = cacheKey;

        try {
            const response = await fetch(url, { signal });
            this.lastSearchStatus = response.status;

            const data = await response.json();

            // Ignora resposta se o usuário já mudou o texto (evita resultado antigo sobrescrever)
            if (this.searchQuery.trim() !== queryAtRequest) {
                return;
            }

            // A resposta tem estrutura: { success, radios: { results: [...] }, total|count }
            const radios = data.radios?.results || data.radios || [];

            if (!data.success && radios.length === 0 && this.searchOffset === 0) {
                throw new Error('Falha ao buscar rádios');
            }

            // Armazenar total e acumular rádios
            const pageCount = Array.isArray(radios) ? radios.length : 0;
            const apiTotal = (typeof data.total === 'number') ? data.total : (typeof data.count === 'number' ? data.count : null);
            const minimalTotal = this.searchOffset + pageCount;
            const hasFullPage = pageCount === this.searchLimit;

            // Se o servidor não informar total, ou informar apenas o tamanho da página,
            // mantemos o botão visível quando veio uma página cheia (assume que pode haver mais).
            if (apiTotal !== null) {
                this.searchTotal = Math.max(apiTotal, minimalTotal + (hasFullPage ? 1 : 0));
            } else {
                this.searchTotal = minimalTotal + (hasFullPage ? 1 : 0);
            }
            this.searchRadios = this.searchOffset === 0 ? radios : [...this.searchRadios, ...radios];

            // Armazena no cache LRU para queries repetidas
            this.setCachedSearch(cacheKeyAtRequest, {
                radios: Array.isArray(radios) ? radios : [],
                searchTotal: this.searchTotal
            });

            this.filteredCategories = this.convertRadiosToCategories(this.searchRadios);
            this.currentCategoryPage = 0;
            this.clearLoadError();
            this.renderSearchResults();
        } catch (err) {
            // Ignora aborts explícitos
            if (err.name === 'AbortError') {return;}

            console.error('❌ Erro ao buscar no servidor:', err);
            this.showLoadError('search');
        } finally {
            // Só finaliza estado de loading se esta ainda for a requisição "ativa".
            // Isso evita que uma busca abortada esconda o loading da nova busca.
            if (this.activeSearchController === myController || this.activeSearchController === null) {
                this.isLoading = false;
                this.showLoading(false);
                this.setSearchInputLoading(false);
                if (this.activeSearchController === myController) {
                    this.activeSearchController = null;
                }
            }
        }
    }

    convertRadiosToCategories(radios) {
        // Converte array de rádios em estrutura de categoria para render igual
        if (!Array.isArray(radios) || radios.length === 0) {
            return [];
        }

        return [{
            title: 'Resultados de busca',
            detail: `${radios.length} rádio(s) encontrada(s)`,
            radios: Array.isArray(radios) ? radios : []
        }];
    }

    renderSearchResults() {
        const container = document.querySelector('#dynamic-radio-grid');
        if (!container) return;

        // É um render de "nova busca" (não um "carregar mais")?
        // O scroll suave só acontece na primeira página de resultados.
        const isFreshSearch = this.searchOffset === 0;

        container.innerHTML = '';

        // Ocultar section title quando há busca
        const sectionTitle = document.querySelector('.radio-grid-section > .section-title');
        if (sectionTitle) {
            sectionTitle.style.display = 'none';
        }

        if (this.filteredCategories && this.filteredCategories.length > 0) {
            this.setSeoRadiosFallbackVisible(false);
        }

        if (!this.filteredCategories || this.filteredCategories.length === 0) {
            container.innerHTML = '<p class="no-radios">Nenhuma rádio encontrada</p>';
            const loadMoreBtn = document.getElementById('load-more-btn');
            if (loadMoreBtn) loadMoreBtn.style.display = 'none';
            if (isFreshSearch) this.scrollToResultsSection();
            return;
        }

        this.filteredCategories.forEach((category) => {
            const section = document.createElement('section');
            section.className = 'radio-category-section radio-section--enter';

            const header = document.createElement('div');
            header.className = 'radio-category-header';
            const categoryTitle = (category.title || 'Resultados').replace(/\s+Sugeridas$/i, '');
            header.innerHTML = `
                <h2 class="category-title">${this.escapeHtml(categoryTitle)}</h2>
                ${category.detail ? `<p class="category-detail">${this.escapeHtml(category.detail)}</p>` : ''}
            `;

            const grid = document.createElement('div');
            grid.className = 'radio-category-grid';

            const radios = Array.isArray(category.radios) ? category.radios : [];
            radios.forEach((radio) => {
                const card = this.createRadioCard(radio);
                grid.appendChild(card);
            });

            section.appendChild(header);
            section.appendChild(grid);
            container.appendChild(section);
        });

        this.setupRadioCardListeners();
        this.staggerRevealCards(container);

        // Controlar botão "Carregar mais" baseado no total de resultados
        const loadMoreBtn = document.getElementById('load-more-btn');
        if (loadMoreBtn) {
            const currentCount = this.searchRadios.length;
            if (currentCount < this.searchTotal) {
                loadMoreBtn.style.display = 'block';
                loadMoreBtn.disabled = false;
                loadMoreBtn.textContent = `Carregar mais rádios`;
            } else {
                loadMoreBtn.style.display = 'none';
            }
        }

        if (isFreshSearch) this.scrollToResultsSection();
    }

    /**
     * Faz scroll suave para a secção de resultados da busca (a grelha).
     * Respeita o header sticky (oferece offset) e prefers-reduced-motion.
     * Só dispara quando a secção não está totalmente visível no topo.
     */
    scrollToResultsSection() {
        const section = document.querySelector('.radio-grid-section');
        if (!section || typeof window === 'undefined') return;

        const header = document.querySelector('.rp-header');
        const headerH = header ? header.offsetHeight : 0;
        const margin = 12;
        const rect = section.getBoundingClientRect();
        const desiredTop = (window.scrollY || window.pageYOffset || 0) + rect.top - headerH - margin;

        // Se já estamos perto/acima do alvo, evita rolar para trás
        const currentScroll = window.scrollY || window.pageYOffset || 0;
        if (Math.abs(currentScroll - desiredTop) < 24) return;

        const prefersReduced =
            window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        try {
            window.scrollTo({
                top: Math.max(0, desiredTop),
                behavior: prefersReduced ? 'auto' : 'smooth',
            });
        } catch (e) {
            // Fallback para browsers antigos sem objeto de opções
            window.scrollTo(0, Math.max(0, desiredTop));
        }
    }

    applyFilter() {
        const query = this.searchQuery.trim().toLowerCase();
        if (!query) {
            this.filteredCategories = this.allCategories;
            this.currentCategoryPage = 0;
            this.renderCategories();
            return;
        }

        this.filteredCategories = this.allCategories
            .map((cat) => {
                const radiosFiltradas = (cat.radios || []).filter((radio) => {
                    const title = (radio.title || '').toLowerCase();
                    const loc = (radio.localizacao || '').toLowerCase();
                    const segmento = (radio.segmento || '').toLowerCase();
                    return title.includes(query) || loc.includes(query) || segmento.includes(query);
                });
                return { ...cat, radios: radiosFiltradas };
            })
            .filter((cat) => (cat.radios || []).length > 0);

        this.currentCategoryPage = 0;
        this.renderCategories();
    }

    renderCategories() {
        const container = document.querySelector('#dynamic-radio-grid');
        if (!container) return;

        container.innerHTML = '';

        // Mostrar section title quando voltando para visualização padrão
        const sectionTitle = document.querySelector('.radio-grid-section > .section-title');
        if (sectionTitle) {
            sectionTitle.style.display = 'block';
        }

        if (!this.filteredCategories || this.filteredCategories.length === 0) {
            container.innerHTML = '<p class="no-radios">Nenhuma rádio encontrada</p>';
            this.updateLoadMoreButton();
            return;
        }

        // Grid dinâmico ok → esconde lista SSR (continua no HTML inicial p/ crawlers)
        this.setSeoRadiosFallbackVisible(false);

        // Calcular quais categorias exibir baseado na paginação
        const startIdx = 0;
        const endIdx = (this.currentCategoryPage + 1) * this.categoriesPerPage;
        const categoriesToShow = this.filteredCategories.slice(startIdx, endIdx);

        categoriesToShow.forEach((category) => {
            // Se for jogo-destaque, renderizar de forma especial
            if (category.tipo === 'jogo-destaque' && Array.isArray(category.jogos) && category.jogos.length > 0) {
                this.renderJogoDestaque(container, category);
                return;
            }

            const section = document.createElement('section');
            section.className = 'radio-category-section radio-section--enter';

            const header = document.createElement('div');
            header.className = 'radio-category-header';
            const categoryTitle = (category.title || 'Categoria').replace(/\s+Sugeridas$/i, '');
            const categoryLandingPath = category.segment_link === '/radios/sertaneja-online'
                ? '/radios/sertaneja-online'
                : '';
            header.innerHTML = `
                <h2 class="category-title">${this.escapeHtml(categoryTitle)}</h2>
                ${categoryLandingPath ? `<a class="radio-category-see-all" href="${this.escapeHtml(categoryLandingPath)}">Ver rádios sertanejas online</a>` : ''}
                ${category.detail ? `<p class="category-detail">${this.escapeHtml(category.detail)}</p>` : ''}
            `;

            const grid = document.createElement('div');
            grid.className = 'radio-category-grid';

            const radios = Array.isArray(category.radios) ? category.radios : [];
            radios.forEach((radio) => {
                const card = this.createRadioCard(radio);
                grid.appendChild(card);
            });

            section.appendChild(header);
            section.appendChild(grid);

            // Botão sutil de "carregar mais" (seta para baixo) por categoria,
            // apenas para categorias vindas do landing (têm "key" e total_items > radios atuais).
            const totalItems = Number(category.total_items || 0);
            if (
                category.key &&
                category.key !== this.favoritesCategoryKey &&
                Array.isArray(category.radios) &&
                totalItems > category.radios.length
            ) {
                const loadMoreWrap = document.createElement('div');
                loadMoreWrap.className = 'radio-category-load-more';
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'radio-category-load-more-btn';
                btn.setAttribute('aria-label', 'Carregar mais rádios desta categoria');
                btn.dataset.categoryKey = category.key;
                btn.innerHTML =
                    '<i class="fas fa-chevron-down" aria-hidden="true"></i>' +
                    '<span class="radio-category-load-more-spinner" aria-hidden="true"></span>';
                btn.addEventListener('click', () => {
                    this.loadMoreCategoryItems(category.key, btn);
                });
                loadMoreWrap.appendChild(btn);
                section.appendChild(loadMoreWrap);
            }

            container.appendChild(section);
        });

        this.setupRadioCardListeners();
        this.updateLoadMoreButton();
        this.loadFavoriteStates();
        this.staggerRevealCards(container);
        this.signalRadiosGridReady();
    }

    signalRadiosGridReady() {
        if (window.__rpRadiosGridReady) return;
        const container = document.querySelector('#dynamic-radio-grid');
        if (!container || !container.children.length) return;
        window.__rpRadiosGridReady = true;
        document.dispatchEvent(new CustomEvent('rp-radios-grid-ready'));
    }

    renderJogoDestaque(container, category) {
        const section = document.createElement('section');
        section.className = 'radio-category-section jogo-destaque-section radio-section--enter';

        const header = document.createElement('div');
        header.className = 'radio-category-header';
        header.innerHTML = `
            <h2 class="category-title">${this.escapeHtml(category.title || 'Jogo em Destaque')}</h2>
            ${category.detail ? `<p class="category-detail">${this.escapeHtml(category.detail)}</p>` : ''}
        `;

        section.appendChild(header);

        // Renderizar cada jogo
        category.jogos.forEach((jogo) => {
            const jogoCard = document.createElement('div');
            jogoCard.className = 'jogo-destaque-card';

            // Logos dos times
            const mandanteLogo = jogo.logo_mandante ? `<img src="${this.escapeHtml(jogo.logo_mandante)}" alt="${this.escapeHtml(jogo.mandante)}" class="time-logo">` : '';
            const visitanteLogo = jogo.logo_visitante ? `<img src="${this.escapeHtml(jogo.logo_visitante)}" alt="${this.escapeHtml(jogo.visitante)}" class="time-logo">` : '';

            jogoCard.innerHTML = `
                <div class="jogo-info">
                    <div class="jogo-live-badge">🔴 AO VIVO</div>
                    <div class="jogo-header">
                        <p class="jogo-competicao">${this.escapeHtml(jogo.competicao)}</p>
                        <p class="jogo-horario">${this.escapeHtml(jogo.info)}</p>
                    </div>
                    <div class="jogo-times">
                        <div class="time-mandante">
                            ${mandanteLogo}
                            <span class="time-nome">${this.escapeHtml(jogo.mandante)}</span>
                        </div>
                        <div class="jogo-versus">VS</div>
                        <div class="time-visitante">
                            ${visitanteLogo}
                            <span class="time-nome">${this.escapeHtml(jogo.visitante)}</span>
                        </div>
                    </div>
                </div>
                <div class="jogo-radios"></div>
            `;

            // Adicionar cards das rádios
            const jogRadios = jogoCard.querySelector('.jogo-radios');
            if (jogo.radios && jogo.radios.length > 0) {
                jogo.radios.forEach((radio) => {
                    const radioCard = this.createRadioCard(radio, true); // true = compact mode
                    jogRadios.appendChild(radioCard);
                });
            }

            section.appendChild(jogoCard);
        });

        container.appendChild(section);
    }

    /**
     * Revela cards/seções com fade + leve slide (respeita prefers-reduced-motion).
     *
     * @param {Element} root Container ou grid onde buscar .radio-card--enter
     * @param {{ baseIndex?: number, sections?: boolean }} options
     */
    finalizeEntryAnimations(sections, cards) {
        sections.forEach((section) => {
            section.classList.remove('radio-section--enter', 'is-visible');
            section.style.removeProperty('--section-stagger');
        });
        cards.forEach((card) => {
            card.classList.remove('radio-card--enter', 'is-visible');
            card.style.removeProperty('--stagger');
        });
    }

    staggerRevealCards(root, options = {}) {
        if (!root || typeof root.querySelectorAll !== 'function') return;

        const animateSections = options.sections !== false;
        const baseIndex = Number(options.baseIndex) || 0;
        const maxStagger = 12;
        const enterDurationMs = 440;
        const staggerStepMs = 48;

        const reduced =
            typeof window !== 'undefined' &&
            window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const sections = animateSections
            ? Array.from(root.querySelectorAll('.radio-category-section.radio-section--enter:not(.is-visible)'))
            : [];
        const cards = Array.from(root.querySelectorAll('.radio-card.radio-card--enter:not(.is-visible)'));

        if (!sections.length && !cards.length) return;

        if (reduced) {
            this.finalizeEntryAnimations(sections, cards);
            return;
        }

        const reveal = () => {
            sections.forEach((section, sectionIndex) => {
                section.style.setProperty('--section-stagger', String(Math.min(sectionIndex, 4)));
                section.classList.add('is-visible');
            });
            cards.forEach((card, index) => {
                card.style.setProperty('--stagger', String(Math.min(baseIndex + index, maxStagger)));
                card.classList.add('is-visible');
            });

            const lastStagger = cards.length
                ? Math.min(baseIndex + cards.length - 1, maxStagger)
                : 0;
            const cleanupDelay = lastStagger * staggerStepMs + enterDurationMs + 80;

            window.setTimeout(() => {
                this.finalizeEntryAnimations(sections, cards);
            }, cleanupDelay);
        };

        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => requestAnimationFrame(reveal));
        } else {
            setTimeout(reveal, 16);
        }
    }

    createRadioCard(radio, compact = false) {
        const card = document.createElement('div');
        card.className = compact
            ? 'radio-card radio-card-compact radio-card--enter'
            : 'radio-card radio-card--enter';
        card.dataset.radioId = radio.id;

        // URL da logo já vem normalizada do backend
        const logoUrl = radio.url_logo || '';
        const bgColor = radio.color || '#f0f0f0';
        const streamUrl = radio.streams && radio.streams.length > 0 ? radio.streams[0].url : '';
        const location = radio.localizacao || radio.location || '';

        // Título com múltiplos fallbacks para diferentes formatos de API
        const displayName =
            radio.title ||
            radio.nome ||
            radio.name ||
            radio.radio_name ||
            (radio.station && radio.station.name) ||
            'Rádio';

        const defaultLogo = (window.RadiosProFavorites && typeof window.RadiosProFavorites.defaultSiteLogoUrl === 'function')
            ? window.RadiosProFavorites.defaultSiteLogoUrl()
            : '/assets/img/header/logo.png';
        if (logoUrl) {
            const probe = new Image();
            probe.onload = () => {
                card.style.backgroundImage = `url('${logoUrl.replace(/'/g, "\\'")}')`;
            };
            probe.onerror = () => {
                card.style.backgroundImage = `url('${defaultLogo.replace(/'/g, "\\'")}')`;
            };
            probe.src = logoUrl;
        } else {
            card.style.backgroundImage = `url('${defaultLogo.replace(/'/g, "\\'")}')`;
        }
        card.style.backgroundSize = 'cover';
        card.style.backgroundPosition = 'center';
        card.style.backgroundColor = bgColor;

        // Criar HTML do dropdown com informações
        const dropdownHtml = this.createDropdownHtml(radio);

        card.innerHTML = `
            <button class="radio-play-btn" aria-label="Tocar ${this.escapeHtml(displayName)}" data-stream-url="${this.escapeHtml(streamUrl)}">
                <svg class="play-icon" xmlns="http://www.w3.org/2000/svg" width="30" height="45" viewBox="0 0 40 45" fill="none">
                    <path d="M37.5287 18.4415C40.4721 20.1409 40.4721 24.3893 37.5287 26.0887L6.62307 43.9321C3.67967 45.6314 0.000423431 43.5072 0.000423431 40.1085V4.42169C0.000423431 1.02295 3.67967 -1.10127 6.62307 0.598103L37.5287 18.4415Z" fill="#000000"/>
                </svg>
                <svg class="pause-icon" xmlns="http://www.w3.org/2000/svg" width="40" height="45" viewBox="0 0 40 45" fill="none" style="display:none;">
                    <rect x="8" y="5" width="10" height="35" rx="2" fill="#000000"/>
                    <rect x="22" y="5" width="10" height="35" rx="2" fill="#000000"/>
                </svg>
            </button>
            <button class="radio-favorite-btn" aria-label="Favoritar rádio" data-radio-id="${radio.id}">
                <svg class="favorite-icon-empty" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                <svg class="favorite-icon-filled" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#ffffff" stroke="#ffffff" stroke-width="2" style="display:none;">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                <span class="radio-favorite-btn-spinner" aria-hidden="true"></span>
            </button>
            ${dropdownHtml}
            <div class="radio-info">
                <a class="radio-name-link" href="${this.escapeHtml(appradioRadioDetailPath(radio.id, displayName))}">
                    <h3 class="radio-name">${this.escapeHtml(displayName)}</h3>
                </a>
                <p class="radio-location">${this.escapeHtml(location)}</p>
            </div>
            <div class="radio-card-loading-overlay" aria-hidden="true">
                <div class="spinner"></div>
                <span>Carregando rádio...</span>
            </div>
        `;

        return card;
    }

    createDropdownHtml(radio) {
        let html = '<div class="radio-dropdown" style="display:none;">';
        html += '<div class="radio-dropdown-content">';
        
        // Slogan
        if (radio.slogan) {
            html += `<div class="dropdown-section">`;
            html += `<h4>Slogan</h4>`;
            html += `<p>${this.escapeHtml(radio.slogan)}</p>`;
            html += `</div>`;
        }
        
        // Description
        if (radio.description) {
            html += `<div class="dropdown-section">`;
            html += `<h4>Descrição</h4>`;
            html += `<p>${this.escapeHtml(radio.description)}</p>`;
            html += `</div>`;
        }
        
        // Segmento
        if (radio.segmento) {
            html += `<div class="dropdown-section">`;
            html += `<h4>Segmento</h4>`;
            html += `<p>${this.escapeHtml(radio.segmento)}</p>`;
            html += `</div>`;
        }
        
        // Contatos
        if (radio.contatos && radio.contatos.length > 0) {
            html += `<div class="dropdown-section">`;
            html += `<h4>Contatos</h4>`;
            html += `<div class="contact-list">`;
            radio.contatos.forEach(contato => {
                const icon = this.getContactIcon(contato.type);
                const displayValue = contato.title || contato.value;
                if (contato.type === 'phone' || contato.type === 'whatsapp') {
                    html += `<div class="contact-item">`;
                    html += `<span class="contact-icon">${icon}</span>`;
                    html += `<a href="tel:${contato.value}">${this.escapeHtml(displayValue)}</a>`;
                    html += `</div>`;
                } else if (contato.value && contato.value.startsWith('http')) {
                    html += `<div class="contact-item">`;
                    html += `<span class="contact-icon">${icon}</span>`;
                    html += `<a href="${this.escapeHtml(contato.value)}" target="_blank" rel="noopener">${this.escapeHtml(displayValue)}</a>`;
                    html += `</div>`;
                } else {
                    html += `<div class="contact-item">`;
                    html += `<span class="contact-icon">${icon}</span>`;
                    html += `<span>${this.escapeHtml(displayValue)}</span>`;
                    html += `</div>`;
                }
            });
            html += `</div>`;
            html += `</div>`;
        }
        
        html += '</div>';
        html += '</div>';
        return html;
    }

    getContactIcon(type) {
        const icons = {
            'phone': '<i class="fas fa-phone"></i>',
            'whatsapp': '<i class="fab fa-whatsapp"></i>',
            'email': '<i class="fas fa-envelope"></i>',
            'facebook': '<i class="fab fa-facebook"></i>',
            'instagram': '<i class="fab fa-instagram"></i>',
            'twitter': '<i class="fab fa-x-twitter"></i>',
            'youtube': '<i class="fab fa-youtube"></i>',
            'website': '<i class="fas fa-globe"></i>'
        };
        return icons[type] || '<i class="fas fa-mobile-alt"></i>';
    }

    setupRadioCardListeners() {
        // Delegação de evento para play: um único listener no container cobre todos os cards
        // (incluindo jogo-destaque), evitando problemas com clones e re-renders
        const grid = document.querySelector('#dynamic-radio-grid');
        if (grid && !this._playClickDelegationBound) {
            this._playClickDelegationBound = true;
            grid.addEventListener('click', (e) => {
                const btn = e.target.closest('.radio-play-btn');
                if (!btn) return;
                e.preventDefault();
                const streamUrl = (btn.dataset.streamUrl || '').trim();
                const radioCard = btn.closest('.radio-card');
                const radioId = radioCard ? radioCard.dataset.radioId : '';
                const radioName = radioCard ? (radioCard.querySelector('.radio-name') && radioCard.querySelector('.radio-name').textContent) || '' : '';

                if (streamUrl) {
                    if (this.currentPlayingId === radioId && window.musicPlayerManager && !window.musicPlayerManager.audio.paused) {
                        this.stopRadio(radioId);
                    } else {
                        if (!this.canChangeRadio()) {
                            this.showRateLimitWarning();
                            return;
                        }
                        this.playRadio(streamUrl, radioId, radioName);
                    }
                } else {
                    console.warn('[RadioManager] Rádio sem URL de stream:', radioName || radioId || 'card');
                }
            });
        }

        // Adicionar click no card inteiro para redirecionar (exceto nos botões play e favorito)
        const radioCards = document.querySelectorAll('.radio-card');
        radioCards.forEach((card) => {
            card.style.cursor = 'pointer';
            card.addEventListener('click', (e) => {
                // Não redirecionar se clicou em botões específicos
                if (e.target.closest('.radio-play-btn') || e.target.closest('.radio-favorite-btn')) {
                    return;
                }

                // Se o próprio card já está em estado de navegação, ignora cliques repetidos
                if (card.classList.contains('is-navigating')) {
                    return;
                }

                const radioId = card.dataset.radioId;
                if (!radioId) {
                    console.error('Radio ID not found');
                    return;
                }

                // Remover estado de navegação de qualquer outro card já marcado
                if (this.currentNavigatingCard && this.currentNavigatingCard !== card) {
                    this.currentNavigatingCard.classList.remove('is-navigating');
                }

                // Feedback visual apenas no card clicado enquanto a página de detalhes carrega
                card.classList.add('is-navigating');
                this.currentNavigatingCard = card;

                // Link real no título: deixa o browser seguir o href (SEO + middle-click)
                if (e.target.closest('a.radio-name-link')) {
                    return;
                }

                const nameEl = card.querySelector('.radio-name');
                const displayName = nameEl ? nameEl.textContent.trim() : '';
                const url = appradioRadioDetailPath(radioId, displayName);

                try {
                    sessionStorage.setItem('appradio_radio_autoplay_v1', JSON.stringify({
                        v: 1,
                        radioId: String(radioId),
                        ts: Date.now()
                    }));
                } catch (storageErr) { /* private mode / quota */ }

                const mpm = window.musicPlayerManager;
                if (mpm && mpm.currentRadio && String(mpm.currentRadio.id) === String(radioId)
                    && mpm.audio && !mpm.audio.paused && mpm.currentRadio.streamUrl) {
                    try {
                        sessionStorage.setItem('appradio_stream_handoff_v1', JSON.stringify({
                            v: 1,
                            streamUrl: mpm.currentRadio.streamUrl,
                            radioId: parseInt(radioId, 10) || 0,
                            radioName: displayName || mpm.currentRadio.name || '',
                            streamTitle: '',
                            volume: typeof mpm.audio.volume === 'number' ? mpm.audio.volume : 0.7,
                            wasPlaying: true,
                            ts: Date.now()
                        }));
                    } catch (handoffErr) { /* ignore */ }
                }

                window.location.href = url;
            });
        });
        
        const favoriteButtons = document.querySelectorAll('.radio-favorite-btn');
        favoriteButtons.forEach((btn) => {
            btn.replaceWith(btn.cloneNode(true));
        });
        
        // Adicionar listeners para favoritar
        document.querySelectorAll('.radio-favorite-btn').forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const radioCard = btn.closest('.radio-card');
                const radioId = btn.dataset.radioId;
                
                if (radioCard && radioId) {
                    this.toggleFavorite(radioCard, radioId);
                }
            });
        });
    }

    loadRadioInfo(radioId, radioCard) {
        // Marca como carregando
        const dropdown = radioCard.querySelector('.radio-dropdown');
        const dropdownContent = dropdown.querySelector('.radio-dropdown-content');
        
        // Adicionar indicador de carregamento
        const loadingHtml = '<div class="loading-info" style="text-align: center; padding: 20px;"><p>Carregando informações...</p></div>';
        
        fetch(appradioPublicApiUrl(`/ajax/radio-search.php?action=get-radio-info&id=${radioId}`))
            .then(response => response.json())
            .then(data => {
                if (data.success && data.radio) {
                    const radio = data.radio;
                    
                    // Construir HTML com informações adicionais
                    let html = '';
                    
                    // Slogan
                    if (radio.slogan) {
                        html += `<div class="dropdown-section">`;
                        html += `<h4>Slogan</h4>`;
                        html += `<p>${this.escapeHtml(radio.slogan)}</p>`;
                        html += `</div>`;
                    }
                    
                    // Description
                    if (radio.description) {
                        html += `<div class="dropdown-section">`;
                        html += `<h4>Descrição</h4>`;
                        html += `<p>${this.escapeHtml(radio.description)}</p>`;
                        html += `</div>`;
                    }
                    
                    // Segmento
                    if (radio.segmento) {
                        html += `<div class="dropdown-section">`;
                        html += `<h4>Segmento</h4>`;
                        html += `<p>${this.escapeHtml(radio.segmento)}</p>`;
                        html += `</div>`;
                    }
                    
                    // Localização
                    if (radio.localizacao) {
                        html += `<div class="dropdown-section">`;
                        html += `<h4>Localização</h4>`;
                        html += `<p>${this.escapeHtml(radio.localizacao)}</p>`;
                        html += `</div>`;
                    }
                    
                    // Contatos
                    if (radio.contatos && radio.contatos.length > 0) {
                        html += `<div class="dropdown-section">`;
                        html += `<h4>Contatos</h4>`;
                        html += `<div class="contact-list">`;
                        radio.contatos.forEach(contato => {
                            const icon = this.getContactIcon(contato.type);
                            const displayValue = contato.title || contato.value;
                            if (contato.type === 'phone' || contato.type === 'whatsapp') {
                                html += `<div class="contact-item">`;
                                html += `<span class="contact-icon">${icon}</span>`;
                                html += `<a href="tel:${contato.value}">${this.escapeHtml(displayValue)}</a>`;
                                html += `</div>`;
                            } else if (contato.value && contato.value.startsWith('http')) {
                                html += `<div class="contact-item">`;
                                html += `<span class="contact-icon">${icon}</span>`;
                                html += `<a href="${this.escapeHtml(contato.value)}" target="_blank" rel="noopener">${this.escapeHtml(displayValue)}</a>`;
                                html += `</div>`;
                            } else {
                                html += `<div class="contact-item">`;
                                html += `<span class="contact-icon">${icon}</span>`;
                                html += `<span>${this.escapeHtml(displayValue)}</span>`;
                                html += `</div>`;
                            }
                        });
                        html += `</div>`;
                        html += `</div>`;
                    }
                    
                    // Se não houver nenhuma informação adicional
                    if (!html) {
                        html = '<div class="dropdown-section"><p>Sem informações adicionais disponíveis</p></div>';
                    }
                    
                    // Atualizar o dropdown com as novas informações
                    dropdownContent.innerHTML = html;
                    radioCard.dataset.infoLoaded = 'true';
                    
                } else {
                    dropdownContent.innerHTML = '<div class="dropdown-section"><p>Erro ao carregar informações</p></div>';
                }
            })
            .catch(error => {
                console.error('Erro ao buscar informações:', error);
                dropdownContent.innerHTML = '<div class="dropdown-section"><p>Erro ao carregar informações</p></div>';
            });
    }

    playRadio(streamUrl, radioId, radioName, urlLogo) {
        // Atualizar estado visual do botão anterior
        if (this.currentPlayingId) {
            this.updatePlayButton(this.currentPlayingId, false);
        }

        // Atualizar estado atual
        this.currentPlayingId = radioId;
        this.lastRadioChangeTime = Date.now();
        this.updatePlayButton(radioId, true);

        var logo = (urlLogo || '').trim();
        if (!logo) {
            var card = document.querySelector('.radio-card[data-radio-id="' + radioId + '"]');
            if (card) {
                var cardData = this.buildRadioDataFromCard(card, radioId);
                logo = (cardData.url_logo || '').trim();
            }
        }

        const event = new CustomEvent('radio-play', {
            detail: { streamUrl, radioId, radioName, urlLogo: logo }
        });
        document.dispatchEvent(event);
    }

    stopRadio(radioId) {
        this.currentPlayingId = null;
        this.updatePlayButton(radioId, false);

        const event = new CustomEvent('radio-stop', {
            detail: { radioId }
        });
        document.dispatchEvent(event);
    }

    updatePlayButton(radioId, isPlaying) {
        const radioCard = document.querySelector(`.radio-card[data-radio-id="${radioId}"]`);
        if (!radioCard) return;

        const playBtn = radioCard.querySelector('.radio-play-btn');
        const playIcon = playBtn.querySelector('.play-icon');
        const pauseIcon = playBtn.querySelector('.pause-icon');

        if (isPlaying) {
            playIcon.style.display = 'none';
            pauseIcon.style.display = 'block';
            radioCard.classList.add('playing');
        } else {
            playIcon.style.display = 'block';
            pauseIcon.style.display = 'none';
            radioCard.classList.remove('playing');
        }
    }
    
    setFavoriteButtonLoading(btn, loading) {
        if (!btn) return;
        if (loading) {
            btn.classList.add('loading');
            btn.disabled = true;
        } else {
            btn.classList.remove('loading');
            btn.disabled = false;
        }
    }

    buildRadioDataFromCard(radioCard, radioId) {
        const bg = radioCard.style.backgroundImage || '';
        const logoMatch = bg.match(/url\(['"]?(.*?)['"]?\)/);
        const source = {
            id: radioId,
            title: radioCard.querySelector('.radio-name')?.textContent || '',
            url_logo: logoMatch ? logoMatch[1] : '',
            localizacao: radioCard.querySelector('.radio-location')?.textContent || '',
            segmento: radioCard.querySelector('.radio-dropdown .dropdown-section h4')?.nextElementSibling?.textContent || '',
            streams: []
        };

        const streamUrl = radioCard.querySelector('.radio-play-btn')?.dataset.streamUrl;
        if (streamUrl) {
            source.streams.push({ url: streamUrl });
        }

        if (typeof window !== 'undefined' && window.RadiosProFavorites) {
            return window.RadiosProFavorites.buildPayload(source);
        }
        return source;
    }

    readFavoriteStateFromButton(btn) {
        if (!btn) return false;
        if (btn.classList.contains('is-favorited')) return true;
        const filledIcon = btn.querySelector('.favorite-icon-filled');
        if (!filledIcon) return false;
        if (filledIcon.hidden) return false;
        return filledIcon.style.display !== 'none';
    }

    applyFavoriteStateToButton(btn, isFavorited) {
        if (!btn) return;
        btn.classList.toggle('is-favorited', !!isFavorited);
        btn.setAttribute('aria-pressed', isFavorited ? 'true' : 'false');
        const emptyIcon = btn.querySelector('.favorite-icon-empty');
        const filledIcon = btn.querySelector('.favorite-icon-filled');
        if (!emptyIcon || !filledIcon) return;
        if (filledIcon.tagName === 'I' || filledIcon.tagName === 'i') {
            emptyIcon.hidden = !!isFavorited;
            filledIcon.hidden = !isFavorited;
            return;
        }
        emptyIcon.style.display = isFavorited ? 'none' : 'block';
        filledIcon.style.display = isFavorited ? 'block' : 'none';
    }

    notifyFavoriteChanged(radioId, isFavorited) {
        document.dispatchEvent(new CustomEvent('radiospro:favorite-changed', {
            detail: {
                radioId: String(radioId),
                isFavorite: !!isFavorited
            }
        }));
    }

    refreshFavoriteButton(btn, radioId) {
        if (!btn || !radioId) return;

        const card = document.querySelector(`.radio-card[data-radio-id="${radioId}"]`);
        if (card) {
            const cardBtn = card.querySelector('.radio-favorite-btn');
            if (cardBtn) {
                this.applyFavoriteStateToButton(btn, this.readFavoriteStateFromButton(cardBtn));
                return;
            }
        }

        const hasServerSession = !!this.getCookie('RPRO_USER_UID');
        if (!hasServerSession) {
            const isFav = this.getLocalFavorites().some((fav) => String(fav.id) === String(radioId));
            this.applyFavoriteStateToButton(btn, isFav);
            return;
        }

        fetch(`/ajax/ajax-favorites.php?action=check&radioId=${encodeURIComponent(radioId)}`, {
            credentials: 'same-origin'
        })
            .then((response) => response.json())
            .then((data) => {
                this.applyFavoriteStateToButton(btn, !!(data && data.success && data.isFavorite));
            })
            .catch(() => {});
    }

    toggleFavorite(radioCard, radioId) {
        const btn = radioCard.querySelector('.radio-favorite-btn');
        this.toggleFavoriteTarget(radioId, btn, () => this.buildRadioDataFromCard(radioCard, radioId));
    }

    toggleFavoriteTarget(radioId, btn, getRadioData) {
        if (!btn) return;

        const hasServerSession = !!this.getCookie('RPRO_USER_UID');
        const isFavorited = this.readFavoriteStateFromButton(btn);

        this.setFavoriteButtonLoading(btn, true);

        if (isFavorited) {
            if (hasServerSession) {
                const formData = new FormData();
                formData.append('action', 'remove');
                formData.append('radioId', radioId);

                fetch('/ajax/ajax-favorites.php', {
                    method: 'POST',
                    body: formData,
                    credentials: 'same-origin'
                })
                    .then((response) => response.json())
                    .then((data) => {
                        if (data.success) {
                            this.applyFavoriteStateToButton(btn, false);
                            this.showFavoriteToast('Removido dos favoritos', 'success');
                            this.notifyFavoriteChanged(radioId, false);
                        } else if (data.needsLogin) {
                            this.applyFavoriteStateToButton(btn, false);
                            this.removeFromLocalFavorites(radioId);
                            this.showFavoriteToast('Removido dos favoritos', 'success');
                            this.notifyFavoriteChanged(radioId, false);
                        } else {
                            this.showFavoriteToast(data.error || 'Erro ao remover', 'error');
                        }
                    })
                    .catch(() => {
                        this.showFavoriteToast('Erro ao remover dos favoritos', 'error');
                    })
                    .finally(() => {
                        this.setFavoriteButtonLoading(btn, false);
                    });
            } else {
                this.removeFromLocalFavorites(radioId);
                this.applyFavoriteStateToButton(btn, false);
                this.showFavoriteToast('Removido dos favoritos', 'success');
                this.notifyFavoriteChanged(radioId, false);
                this.setFavoriteButtonLoading(btn, false);
            }
            return;
        }

        const radioData = typeof getRadioData === 'function' ? getRadioData() : null;
        if (!radioData) {
            this.setFavoriteButtonLoading(btn, false);
            return;
        }
        const payload = (window.RadiosProFavorites && typeof window.RadiosProFavorites.buildPayload === 'function')
            ? (window.RadiosProFavorites.buildPayload(radioData) || radioData)
            : radioData;

        if (hasServerSession) {
            const formData = new FormData();
            formData.append('action', 'add');
            formData.append('radioId', radioId);
            formData.append('radioData', JSON.stringify(payload));

            fetch('/ajax/ajax-favorites.php', {
                method: 'POST',
                body: formData,
                credentials: 'same-origin'
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.success) {
                        this.applyFavoriteStateToButton(btn, true);
                        this.showFavoriteToast('Adicionado aos favoritos!', 'success');
                        this.notifyFavoriteChanged(radioId, true);
                    } else if (data.needsLogin) {
                        this.applyFavoriteStateToButton(btn, true);
                        this.addToLocalFavorites(payload);
                        this.showFavoriteToast('Adicionado aos favoritos!', 'success');
                        this.notifyFavoriteChanged(radioId, true);
                    } else {
                        this.showFavoriteToast(data.error || 'Erro ao adicionar', 'error');
                    }
                })
                .catch(() => {
                    this.showFavoriteToast('Erro ao adicionar aos favoritos', 'error');
                })
                .finally(() => {
                    this.setFavoriteButtonLoading(btn, false);
                    this.loadFavoriteStates();
                });
            return;
        }

        this.addToLocalFavorites(payload);
        this.applyFavoriteStateToButton(btn, true);
        const syncToastShown = localStorage.getItem('syncToastShown');
        if (!syncToastShown) {
            this.showFavoriteToast('Adicionado aos favoritos! Faça login para sincronizar em todos os seus dispositivos.', 'success');
            localStorage.setItem('syncToastShown', 'true');
        } else {
            this.showFavoriteToast('Adicionado aos favoritos!', 'success');
        }
        this.notifyFavoriteChanged(radioId, true);
        this.setFavoriteButtonLoading(btn, false);
    }

    getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    }

    getFirebaseUid() {
        return this.getCookie('RPRO_USER_UID') || localStorage.getItem('FIREBASE_UID');
    }
    
    clearAuthData() {
        // Limpar cookies
        document.cookie = 'FIREBASE_UID=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        document.cookie = 'FIREBASE_ID_TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        document.cookie = 'SESSION_TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        
        // Limpar localStorage
        localStorage.removeItem('FIREBASE_UID');
        localStorage.removeItem('FIREBASE_ID_TOKEN');
        localStorage.removeItem('SESSION_TOKEN');
        localStorage.removeItem('syncToastShown');
    }

    // Funções para gerenciar favoritos em localStorage (para usuários não logados)
    getLocalFavorites() {
        const favorites = localStorage.getItem('radiosFavoritas');
        if (!favorites) return [];
        try {
            const parsed = JSON.parse(favorites);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }

    getLocalFavoritesOwnerUid() {
        return localStorage.getItem('radiosFavoritasOwnerUid');
    }

    setLocalFavoritesOwnerUid(uid) {
        if (!uid) {
            localStorage.removeItem('radiosFavoritasOwnerUid');
            return;
        }
        localStorage.setItem('radiosFavoritasOwnerUid', uid);
    }

    addToLocalFavorites(radioData) {
        const ownerUid = this.getFirebaseUid() || 'anonymous';
        const storedOwnerUid = this.getLocalFavoritesOwnerUid();
        if (storedOwnerUid && storedOwnerUid !== ownerUid) {
            localStorage.removeItem('radiosFavoritas');
        }

        const favorites = this.getLocalFavorites();
        const exists = favorites.some(fav => fav.id === radioData.id);
        if (!exists) {
            favorites.push(radioData);
            localStorage.setItem('radiosFavoritas', JSON.stringify(favorites));
            this.setLocalFavoritesOwnerUid(ownerUid);
        }
    }

    removeFromLocalFavorites(radioId) {
        const favorites = this.getLocalFavorites();
        const filtered = favorites.filter(fav => fav.id !== radioId);
        if (filtered.length === 0) {
            localStorage.removeItem('radiosFavoritas');
            this.setLocalFavoritesOwnerUid(null);
            return;
        }

        localStorage.setItem('radiosFavoritas', JSON.stringify(filtered));
    }

    // Sincronizar favoritos locais com servidor após login
    syncLocalFavoritesToServer(authenticatedUid = null) {
        const hasServerSession = !!this.getCookie('RPRO_USER_UID');
        const currentUid = authenticatedUid || this.getCookie('RPRO_USER_UID') || this.getFirebaseUid();
        if (!hasServerSession || !currentUid) return Promise.resolve(false);

        const ownerUid = this.getLocalFavoritesOwnerUid();
        if (ownerUid && ownerUid !== 'anonymous' && ownerUid !== currentUid) {
            localStorage.removeItem('radiosFavoritas');
            localStorage.removeItem('syncToastShown');
            this.setLocalFavoritesOwnerUid(null);
            return Promise.resolve(false);
        }

        const localFavorites = this.getLocalFavorites();
        if (localFavorites.length === 0) return Promise.resolve(false);

        // Sincronizar cada favorito com o servidor
        const syncRequests = localFavorites.map((radioData) => {
            const payload = (window.RadiosProFavorites && typeof window.RadiosProFavorites.buildPayload === 'function')
                ? (window.RadiosProFavorites.buildPayload(radioData) || radioData)
                : radioData;
            const formData = new FormData();
            formData.append('action', 'add');
            formData.append('radioId', payload.id);
            formData.append('radioData', JSON.stringify(payload));
            formData.append('fromSync', 'true');

            return fetch('/ajax/ajax-favorites.php', {
                method: 'POST',
                body: formData,
                credentials: 'same-origin'
            })
                .then(response => response.json())
                .then((data) => data && data.success === true)
                .catch(() => false);
        });

        return Promise.all(syncRequests).then((results) => {
            const allSynced = results.every(Boolean);
            if (allSynced) {
                localStorage.removeItem('radiosFavoritas');
                localStorage.removeItem('syncToastShown');
                this.setLocalFavoritesOwnerUid(null);
            }
            return allSynced;
        });
    }
    
    showFavoriteToast(message, type = 'info') {
        // Criar toast simples
        const toast = document.createElement('div');
        toast.className = `favorite-toast favorite-toast-${type}`;
        toast.textContent = message;
        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 12px 24px;
            background: ${type === 'success' ? '#4caf50' : type === 'error' ? '#f44336' : '#ff9800'};
            color: white;
            border-radius: 4px;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            z-index: 10000;
            animation: slideIn 0.3s ease-out;
        `;
        
        document.body.appendChild(toast);
        
        setTimeout(() => {
            toast.style.animation = 'slideOut 0.3s ease-out';
            setTimeout(() => toast.remove(), 300);
        }, 3000);
    }
    
    canChangeRadio() {
        const now = Date.now();
        const timeSinceLastChange = now - this.lastRadioChangeTime;
        return timeSinceLastChange >= this.radioChangeCooldown;
    }
    
    showRateLimitWarning() {
        // Prevenir múltiplos toasts
        if (this.rateLimitToast) {
            return;
        }
        
        const remainingTime = Math.ceil((this.radioChangeCooldown - (Date.now() - this.lastRadioChangeTime)) / 1000);
        const message = `⏱️ Aguarde ${remainingTime}s antes de trocar de rádio`;
        
        const toast = document.createElement('div');
        toast.className = 'favorite-toast favorite-toast-warning';
        toast.textContent = message;
        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 12px 24px;
            background: #ff9800;
            color: white;
            border-radius: 4px;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            z-index: 10000;
            font-weight: 600;
            animation: slideIn 0.3s ease-out;
        `;
        document.body.appendChild(toast);
        this.rateLimitToast = toast;

        setTimeout(() => {
            toast.style.animation = 'slideOut 0.3s ease-out';
            setTimeout(() => {
                toast.remove();
                this.rateLimitToast = null;
            }, 300);
        }, 2000);
    }
    
    loadFavoriteStates() {
        // Verificar se há sessão válida no servidor
        const firebaseUid = this.getFirebaseUid();
        const hasServerSession = !!this.getCookie('RPRO_USER_UID');

        // Sessão recém-criada (SSO callback redirecionou de volta) + favoritos
        // anônimos no localStorage = sincroniza pro Firestore antes de pintar.
        if (hasServerSession && firebaseUid) {
            const localFavorites = this.getLocalFavorites();
            const ownerUid = this.getLocalFavoritesOwnerUid();
            const needsSync = localFavorites.length > 0 &&
                (!ownerUid || ownerUid === 'anonymous' || ownerUid === firebaseUid);
            if (needsSync) {
                this.syncLocalFavoritesToServer(firebaseUid).then(() => {
                    // Após sync, recarrega para refletir o estado server-side
                    // canônico (e capturar deltas que já existiam na conta).
                    this._fetchAndPaintServerFavorites();
                });
                return;
            }
        }

        // Se não há sessão no servidor, carregar favoritos apenas do localStorage
        if (!hasServerSession) {
            const localFavorites = this.getLocalFavorites();
            localFavorites.forEach(favorite => {
                const radioId = favorite.id;
                const radioCard = document.querySelector(`.radio-card[data-radio-id="${radioId}"]`);
                if (radioCard) {
                    const emptyIcon = radioCard.querySelector('.favorite-icon-empty');
                    const filledIcon = radioCard.querySelector('.favorite-icon-filled');
                    if (emptyIcon && filledIcon) {
                        emptyIcon.style.display = 'none';
                        filledIcon.style.display = 'block';
                    }
                }
            });
            return;
        }

        this._fetchAndPaintServerFavorites();
    }

    _fetchAndPaintServerFavorites() {
        return fetch('/ajax/ajax-favorites.php?action=list', { credentials: 'same-origin' })
            .then(response => response.json())
            .then(data => {
                if (data.success && Array.isArray(data.favorites)) {
                    data.favorites.forEach(favorite => {
                        const radioId = favorite.id || favorite.radioId;
                        const radioCard = document.querySelector(`.radio-card[data-radio-id="${radioId}"]`);
                        if (radioCard) {
                            const btn = radioCard.querySelector('.radio-favorite-btn');
                            if (btn) {
                                const emptyIcon = btn.querySelector('.favorite-icon-empty');
                                const filledIcon = btn.querySelector('.favorite-icon-filled');
                                if (emptyIcon && filledIcon) {
                                    emptyIcon.style.display = 'none';
                                    filledIcon.style.display = 'block';
                                }
                            }
                        }
                    });
                } else if (data.needsLogin) {
                    try {
                        if (typeof appradioRefreshFirebaseIdTokenCookie === 'function') {
                            appradioRefreshFirebaseIdTokenCookie(false);
                        }
                    } catch (e) {
                        // silencioso
                    }
                }
            })
            .catch(() => {});
    }

    showLoading(show) {
        const loading = document.getElementById('loading-indicator');
        const loadMoreBtn = document.getElementById('load-more-btn');
        const pagination = document.querySelector('.pagination-controls');
        const isLoadMoreSearch = this.searchMode === 'server' && this.searchOffset > 0 && this.searchRadios.length > 0;

        let inlineLoading = document.getElementById('load-more-loading-indicator');
        if (!inlineLoading && pagination) {
            inlineLoading = document.createElement('div');
            inlineLoading.id = 'load-more-loading-indicator';
            inlineLoading.className = 'loading-indicator loading-indicator-inline';
            inlineLoading.style.display = 'none';
            inlineLoading.innerHTML = '<div class="spinner"></div><p>Carregando mais rádios...</p>';
            if (loadMoreBtn) {
                pagination.insertBefore(inlineLoading, loadMoreBtn);
            } else {
                pagination.appendChild(inlineLoading);
            }
        }

        if (show && isLoadMoreSearch) {
            if (loading) loading.style.display = 'none';
            if (inlineLoading) inlineLoading.style.display = 'flex';
            if (loadMoreBtn) {
                loadMoreBtn.disabled = true;
                loadMoreBtn.classList.add('is-loading');
            }
            return;
        }

        if (inlineLoading) inlineLoading.style.display = 'none';
        if (loadMoreBtn) {
            loadMoreBtn.classList.remove('is-loading');
            if (!show) loadMoreBtn.disabled = false;
        }
        if (loading) {
            loading.style.display = show ? 'flex' : 'none';
        }
    }

    showLoadError(context) {
        this.loadErrorActive = true;
        this.loadErrorContext = context === 'search' ? 'search' : 'categories';

        // Sem grid dinâmico: mantém/reexibe a lista SSR com links /radio/
        this.setSeoRadiosFallbackVisible(true);

        const container = document.querySelector('#dynamic-radio-grid');
        if (!container) return;

        const title = this.getI18n('loadErrorTitle', 'Falha de conexão');
        const message = this.getI18n(
            'loadErrorNetwork',
            'Não foi possível carregar as rádios. Verifique sua internet e tente novamente.'
        );
        const retryLabel = this.getI18n('loadErrorRetry', 'Tentar novamente');
        const reloadLabel = this.getI18n('loadErrorReload', 'Recarregar página');

        container.innerHTML = '';
        const panel = document.createElement('div');
        panel.className = 'radio-load-error';
        panel.setAttribute('role', 'alert');
        panel.innerHTML = `
            <div class="radio-load-error__icon" aria-hidden="true"><i class="fas fa-wifi"></i></div>
            <p class="radio-load-error__title">${this.escapeHtml(title)}</p>
            <p class="radio-load-error__message">${this.escapeHtml(message)}</p>
            <div class="radio-load-error__actions">
                <button type="button" class="radio-load-error__btn radio-load-error__btn--primary" data-load-retry>
                    <i class="fas fa-rotate-right" aria-hidden="true"></i>
                    ${this.escapeHtml(retryLabel)}
                </button>
                <button type="button" class="radio-load-error__btn radio-load-error__btn--ghost" data-load-reload>
                    <i class="fas fa-arrows-rotate" aria-hidden="true"></i>
                    ${this.escapeHtml(reloadLabel)}
                </button>
            </div>
        `;
        container.appendChild(panel);

        const retryBtn = panel.querySelector('[data-load-retry]');
        const reloadBtn = panel.querySelector('[data-load-reload]');
        if (retryBtn) {
            retryBtn.addEventListener('click', () => this.retryAfterLoadError());
        }
        if (reloadBtn) {
            reloadBtn.addEventListener('click', () => window.location.reload());
        }

        const loadMoreBtn = document.getElementById('load-more-btn');
        if (loadMoreBtn) loadMoreBtn.style.display = 'none';

        // Sinaliza que já há conteúdo (a tela de erro) para a splash poder sair.
        document.dispatchEvent(new CustomEvent('rp-radios-load-error'));
    }

    escapeHtml(text) {
        if (typeof text !== 'string') return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

export function initRadioManager() {
    window.radioManager = new RadioManager();
    window.addEventListener('appradio-firebase-token-refreshed', () => {
        if (window.radioManager && typeof window.radioManager.loadFavoriteStates === 'function') {
            window.radioManager.loadFavoriteStates();
        }
    });
    return window.radioManager;
}

